import {
  AudioOutlined,
  CheckCircleFilled,
  CloseOutlined,
  DownloadOutlined,
  FileTextOutlined,
  HeartOutlined,
  LinkOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined
} from '@ant-design/icons';
import { Button, Empty, Modal, Typography } from 'antd';
import eternalLogo from 'assets/logo-text-full.svg';
import { Artifact, PublicArtifactBranding } from 'components/Artifacts/types';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { useCompanyContext } from 'contexts/CompanyContext';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectAuthToken, selectAuthUser } from 'store/authSlice';
import { buildBrandedInviteSignupHref } from 'utils/brandedInviteSignup';
import { isEternalOnlyBranding, toAlphaColor } from 'utils/branding';
import { downloadFileFromApi } from 'utils/downloadFile';
import './PublicArtifactCollectionRenderer.css';

const { Paragraph, Text, Title } = Typography;

const ETERNAL_PRIMARY_COLOR = '#5449dc';
const ETERNAL_PRIMARY_LIGHTER_COLOR = '#eeedfb';

const EXPANDABLE_TEXT_PREVIEW_LENGTH = 280;
const SLIDESHOW_FALLBACK_ADVANCE_MS = 6500;
const SLIDESHOW_CONTROLS_IDLE_MS = 2200;

type PublicArtifactCollectionRendererProps = {
  allowDownloads?: boolean;
  branding: PublicArtifactBranding;
  items: Artifact[];
  downloadZipUrl?: string | null;
  publicToken?: string;
  signupCode?: string | null;
  showKeepCard?: boolean;
  showTopbar?: boolean;
};

const getPatientDisplayName = (branding: PublicArtifactBranding) => {
  return (
    branding.fullName?.trim() ||
    [branding.firstName, branding.lastName].filter(Boolean).join(' ').trim() ||
    'Your loved one'
  );
};

const getTopbarLogo = (branding: PublicArtifactBranding) => {
  const tenantOrganizationName = branding.tenantOrganizationName?.trim();
  const tenantOrganizationLogoUrl = branding.tenantOrganizationLogoUrl?.trim();

  if (tenantOrganizationLogoUrl) {
    return {
      src: tenantOrganizationLogoUrl,
      alt: `${tenantOrganizationName || 'Partner'} logo`
    };
  }

  return {
    src: eternalLogo,
    alt: 'Eternal logo'
  };
};

const getMemoryLabel = (artifact: Artifact) => {
  const parts = [artifact.memoryDateLabel, artifact.memoryDate].filter(Boolean);
  return parts.length ? parts.join(' • ') : 'Date not provided';
};

const getTranscriptCopy = (artifact: Artifact) => {
  switch (artifact.transcript.status) {
    case 'COMPLETED':
      return {
        heading: 'Transcript Excerpt',
        body: artifact.transcript.text || artifact.transcript.generatedText || 'Transcript not available yet.'
      } as const;
    case 'FAILED':
      return { heading: 'Transcript unavailable', body: 'Transcript temporarily unavailable.' } as const;
    case 'PROCESSING':
    case 'QUEUED':
      return {
        heading: 'Transcript in progress',
        body: 'A transcript is still being prepared for this memory.'
      } as const;
    default:
      return { heading: 'Transcript', body: 'No transcript was shared for this memory.' } as const;
  }
};

const getArtifactRenderKey = (artifact: Artifact, index: number) => {
  const artifactId = artifact.id?.trim();

  if (artifactId) {
    return artifactId;
  }

  return [
    'artifact',
    index,
    artifact.displayOrder,
    artifact.title?.trim() || 'untitled',
    artifact.image?.id || 'image',
    artifact.memoryDate || 'date'
  ].join('::');
};

const sanitizeFilenamePart = (value: string | null | undefined, fallback: string) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return fallback;
  }

  const sanitized = trimmed
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return sanitized || fallback;
};

const getCollectionDownloadUrl = (token: string) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  return apiBaseUrl
    ? new URL(`/public/artifacts/${encodeURIComponent(token)}/download`, apiBaseUrl).toString()
    : `/public/artifacts/${encodeURIComponent(token)}/download`;
};

export function PublicArtifactCollectionRenderer({
  allowDownloads = false,
  branding,
  items,
  downloadZipUrl,
  publicToken,
  signupCode,
  showKeepCard = true,
  showTopbar = true
}: PublicArtifactCollectionRendererProps) {
  const { companyConfig } = useCompanyContext();
  const accessToken = useSelector(selectAuthToken);
  const user = useSelector(selectAuthUser);
  const location = useLocation();
  const orderedItems = useMemo(() => [...items].sort((left, right) => left.displayOrder - right.displayOrder), [items]);
  const keyedItems = useMemo(
    () => orderedItems.map((artifact, index) => ({ artifact, renderKey: getArtifactRenderKey(artifact, index) })),
    [orderedItems]
  );
  const [selectedArtifactKey, setSelectedArtifactKey] = useState<string | null>(keyedItems[0]?.renderKey || null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [isExpandedArtifactOpen, setIsExpandedArtifactOpen] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [zipDownloadError, setZipDownloadError] = useState('');
  const [isSlideshowOpen, setIsSlideshowOpen] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [isSlideshowPaused, setIsSlideshowPaused] = useState(false);
  const [areSlideshowControlsVisible, setAreSlideshowControlsVisible] = useState(true);
  const [isSlideshowAudioUnavailable, setIsSlideshowAudioUnavailable] = useState(false);
  const slideshowControlsHideTimeoutRef = useRef<number | null>(null);
  const slideshowAdvanceTimeoutRef = useRef<number | null>(null);
  const slideshowAudioRef = useRef<HTMLAudioElement | null>(null);
  const slideshowDialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!keyedItems.length) {
      setSelectedArtifactKey(null);
      return;
    }

    if (!selectedArtifactKey || !keyedItems.some((item) => item.renderKey === selectedArtifactKey)) {
      setSelectedArtifactKey(keyedItems[0].renderKey);
    }
  }, [keyedItems, selectedArtifactKey]);

  useEffect(() => {
    setIsDescriptionExpanded(false);
    setIsTranscriptExpanded(false);
    setIsExpandedArtifactOpen(false);
  }, [selectedArtifactKey]);

  useEffect(() => {
    return () => {
      if (slideshowControlsHideTimeoutRef.current) {
        window.clearTimeout(slideshowControlsHideTimeoutRef.current);
      }

      if (slideshowAdvanceTimeoutRef.current) {
        window.clearTimeout(slideshowAdvanceTimeoutRef.current);
      }
    };
  }, []);

  const selectedIndex = keyedItems.findIndex((item) => item.renderKey === selectedArtifactKey);
  const selectedItem = selectedIndex >= 0 ? keyedItems[selectedIndex] : keyedItems[0] || null;
  const selectedArtifact = selectedItem?.artifact || null;
  const slideshowItem = isSlideshowOpen ? keyedItems[slideshowIndex] || null : null;
  const slideshowArtifact = slideshowItem?.artifact || null;
  const patientName = getPatientDisplayName(branding);
  const tenantOrganizationName = branding.tenantOrganizationName?.trim();
  const companyName = companyConfig.companyName?.trim();
  const companyLogoUrl = companyConfig.logoUrl?.trim();
  const companyPrimaryColor = companyConfig.colorPrimary?.trim();
  const companyPrimaryLighterColor = companyConfig.colorPrimaryLighter?.trim();
  const explicitPrimaryColor = branding.colorPrimary?.trim();
  const explicitPrimaryLighterColor = branding.colorPrimaryLighter?.trim();
  const fallbackBrandName = tenantOrganizationName || companyName;
  const organizationName = fallbackBrandName || 'Eternal';
  const normalizedTenantOrganizationName = tenantOrganizationName?.toLowerCase();
  const normalizedCompanyName = companyName?.toLowerCase();
  const canUseCompanyBrandingColors =
    !tenantOrganizationName ||
    (!!normalizedTenantOrganizationName &&
      !!normalizedCompanyName &&
      normalizedTenantOrganizationName === normalizedCompanyName);
  const useEternalOnlyBranding =
    !(explicitPrimaryColor || explicitPrimaryLighterColor) && isEternalOnlyBranding(fallbackBrandName);
  const fallbackPrimaryColor = canUseCompanyBrandingColors ? companyPrimaryColor : undefined;
  const fallbackPrimaryLighterColor = canUseCompanyBrandingColors ? companyPrimaryLighterColor : undefined;
  const primaryColor = useEternalOnlyBranding
    ? ETERNAL_PRIMARY_COLOR
    : explicitPrimaryColor || fallbackPrimaryColor || ETERNAL_PRIMARY_COLOR;
  const lighterColor = useEternalOnlyBranding
    ? ETERNAL_PRIMARY_LIGHTER_COLOR
    : explicitPrimaryLighterColor || fallbackPrimaryLighterColor || ETERNAL_PRIMARY_LIGHTER_COLOR;
  const collectionStyle: CSSProperties = {
    '--public-artifact-accent-color': primaryColor,
    '--public-artifact-accent-color-light': lighterColor,
    '--public-artifact-accent-border-subtle': toAlphaColor(primaryColor, 0.14),
    '--public-artifact-accent-border-medium': toAlphaColor(primaryColor, 0.15),
    '--public-artifact-accent-border-strong': toAlphaColor(primaryColor, 0.16),
    '--public-artifact-accent-border-stronger': toAlphaColor(primaryColor, 0.28),
    '--public-artifact-accent-border-selection': toAlphaColor(primaryColor, 0.34),
    '--public-artifact-accent-fill-soft': toAlphaColor(primaryColor, 0.4),
    '--public-artifact-accent-shadow': toAlphaColor(primaryColor, 0.08),
    '--public-artifact-accent-shadow-soft': toAlphaColor(primaryColor, 0.06),
    '--public-artifact-accent-shadow-lighter': toAlphaColor(primaryColor, 0.05)
  } as CSSProperties;
  const topbarLogo = getTopbarLogo({
    ...branding,
    tenantOrganizationName: branding.tenantOrganizationName || (canUseCompanyBrandingColors ? companyName : undefined),
    tenantOrganizationLogoUrl:
      branding.tenantOrganizationLogoUrl || (canUseCompanyBrandingColors ? companyLogoUrl : undefined)
  });
  const signupBaseUrl = branding.signupUrl?.trim() || process.env.REACT_APP_MAIN_WEBSITE_URL;
  const signupHref = buildBrandedInviteSignupHref(location, signupBaseUrl, { inviteCode: signupCode });
  const heroTitle = branding.heroTitle?.trim() || `${patientName} — Shared Artifacts`;
  const heroSubtitle =
    branding.heroSubtitle?.trim() ||
    'These meaningful moments were shared to celebrate a life and keep their story close.';
  const primaryButtonStyle: CSSProperties = {
    background: primaryColor,
    borderColor: primaryColor,
    color: '#fff'
  };
  const secondaryButtonColor = explicitPrimaryColor || fallbackPrimaryColor || ETERNAL_PRIMARY_COLOR;
  const secondaryButtonStyle: CSSProperties = {
    backgroundColor: '#fff',
    borderColor: toAlphaColor(secondaryButtonColor, 0.28),
    color: secondaryButtonColor,
    '--public-artifact-secondary-button-border-color': toAlphaColor(secondaryButtonColor, 0.28)
  } as CSSProperties;
  const audioPillStyle: CSSProperties = {
    backgroundColor: lighterColor,
    color: secondaryButtonColor
  };
  const topbarLoveStyle: CSSProperties = {
    color: secondaryButtonColor
  };
  const heroDividerStyle: CSSProperties = {
    '--public-artifact-hero-divider-color': secondaryButtonColor,
    '--public-artifact-hero-divider-line-color': toAlphaColor(secondaryButtonColor, 0.4)
  } as CSSProperties;
  const sharedCheckIconStyle: CSSProperties = {
    color: secondaryButtonColor
  };
  const footerStyle: CSSProperties = {
    '--public-artifact-footer-color': secondaryButtonColor
  } as CSSProperties;
  const keepCardHeartStyle: CSSProperties = {
    background: secondaryButtonColor,
    color: '#fff'
  };

  const trimmedPublicToken = publicToken?.trim() || '';
  const normalizedDownloadZipUrl =
    downloadZipUrl?.trim() || (trimmedPublicToken ? getCollectionDownloadUrl(trimmedPublicToken) : '');
  const canDownloadCollection = allowDownloads && Boolean(normalizedDownloadZipUrl);
  const isSingleArtifact = keyedItems.length === 1;
  const slideshowDescription = slideshowArtifact?.description?.trim() || '';
  const hasSlideshowReadyImage = slideshowArtifact?.image?.status === 'READY' && Boolean(slideshowArtifact.image?.url);
  const hasSlideshowReadyAudio =
    slideshowArtifact?.audio?.status === 'READY' &&
    Boolean(slideshowArtifact.audio?.url) &&
    !isSlideshowAudioUnavailable;
  const slideshowProgressPercent = keyedItems.length ? ((slideshowIndex + 1) / keyedItems.length) * 100 : 0;

  const clearSlideshowControlsHideTimeout = () => {
    if (slideshowControlsHideTimeoutRef.current) {
      window.clearTimeout(slideshowControlsHideTimeoutRef.current);
      slideshowControlsHideTimeoutRef.current = null;
    }
  };

  const clearSlideshowAdvanceTimeout = () => {
    if (slideshowAdvanceTimeoutRef.current) {
      window.clearTimeout(slideshowAdvanceTimeoutRef.current);
      slideshowAdvanceTimeoutRef.current = null;
    }
  };

  const handleSelectArtifact = (artifactKey: string) => {
    setSelectedArtifactKey(artifactKey);
  };

  const closeSlideshow = () => {
    clearSlideshowControlsHideTimeout();
    clearSlideshowAdvanceTimeout();
    slideshowAudioRef.current?.pause();
    setIsSlideshowOpen(false);
    setIsSlideshowPaused(false);
    setAreSlideshowControlsVisible(true);
    setIsSlideshowAudioUnavailable(false);
  };

  const goToSlideshowIndex = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= keyedItems.length) {
      return;
    }

    clearSlideshowAdvanceTimeout();
    setSlideshowIndex(nextIndex);
    setIsSlideshowAudioUnavailable(false);
    setAreSlideshowControlsVisible(true);
  };

  const handleOpenSlideshow = () => {
    if (!keyedItems.length) {
      return;
    }

    setSlideshowIndex(0);
    setSelectedArtifactKey(keyedItems[0].renderKey);
    setIsSlideshowPaused(false);
    setAreSlideshowControlsVisible(true);
    setIsSlideshowAudioUnavailable(false);
    setIsSlideshowOpen(true);
  };

  const handleAdvanceSlideshow = () => {
    if (slideshowIndex >= keyedItems.length - 1) {
      closeSlideshow();
      return;
    }

    goToSlideshowIndex(slideshowIndex + 1);
  };

  const handlePreviousSlideshow = () => {
    if (slideshowIndex === 0) {
      return;
    }

    goToSlideshowIndex(slideshowIndex - 1);
  };

  const handleSlideshowInteraction = () => {
    clearSlideshowControlsHideTimeout();
    setAreSlideshowControlsVisible(true);

    if (isSlideshowPaused) {
      return;
    }

    slideshowControlsHideTimeoutRef.current = window.setTimeout(() => {
      setAreSlideshowControlsVisible(false);
      slideshowControlsHideTimeoutRef.current = null;
    }, SLIDESHOW_CONTROLS_IDLE_MS);
  };

  useEffect(() => {
    if (!isSlideshowOpen) {
      return;
    }

    if (!keyedItems.length) {
      clearSlideshowControlsHideTimeout();
      clearSlideshowAdvanceTimeout();
      slideshowAudioRef.current?.pause();
      setIsSlideshowOpen(false);
      setIsSlideshowPaused(false);
      setAreSlideshowControlsVisible(true);
      setIsSlideshowAudioUnavailable(false);
      return;
    }

    if (slideshowIndex >= keyedItems.length) {
      setSlideshowIndex(keyedItems.length - 1);
    }
  }, [isSlideshowOpen, keyedItems, slideshowIndex]);

  useEffect(() => {
    if (!isSlideshowOpen) {
      return;
    }

    const renderKey = keyedItems[slideshowIndex]?.renderKey;
    if (renderKey) {
      setSelectedArtifactKey(renderKey);
    }
  }, [isSlideshowOpen, keyedItems, slideshowIndex]);

  useEffect(() => {
    if (!isSlideshowOpen) {
      clearSlideshowControlsHideTimeout();
      return;
    }

    if (isSlideshowPaused || !areSlideshowControlsVisible) {
      clearSlideshowControlsHideTimeout();
      return;
    }

    slideshowControlsHideTimeoutRef.current = window.setTimeout(() => {
      setAreSlideshowControlsVisible(false);
      slideshowControlsHideTimeoutRef.current = null;
    }, SLIDESHOW_CONTROLS_IDLE_MS);

    return () => {
      clearSlideshowControlsHideTimeout();
    };
  }, [areSlideshowControlsVisible, isSlideshowOpen, isSlideshowPaused]);

  useEffect(() => {
    if (!isSlideshowOpen || !slideshowArtifact || isSlideshowPaused || hasSlideshowReadyAudio) {
      clearSlideshowAdvanceTimeout();
      return;
    }

    slideshowAdvanceTimeoutRef.current = window.setTimeout(() => {
      setSlideshowIndex((previousIndex) => {
        if (previousIndex >= keyedItems.length - 1) {
          clearSlideshowControlsHideTimeout();
          clearSlideshowAdvanceTimeout();
          slideshowAudioRef.current?.pause();
          setIsSlideshowOpen(false);
          setIsSlideshowPaused(false);
          setAreSlideshowControlsVisible(true);
          setIsSlideshowAudioUnavailable(false);
          return previousIndex;
        }

        return previousIndex + 1;
      });
    }, SLIDESHOW_FALLBACK_ADVANCE_MS);

    return () => {
      clearSlideshowAdvanceTimeout();
    };
  }, [hasSlideshowReadyAudio, isSlideshowOpen, isSlideshowPaused, keyedItems.length, slideshowArtifact]);

  useEffect(() => {
    if (!isSlideshowOpen) {
      slideshowAudioRef.current?.pause();
      return;
    }

    const audioElement = slideshowAudioRef.current;

    if (!audioElement || !hasSlideshowReadyAudio) {
      return;
    }

    if (isSlideshowPaused) {
      audioElement.pause();
      return;
    }

    const playPromise = audioElement.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        setIsSlideshowAudioUnavailable(true);
      });
    }
  }, [hasSlideshowReadyAudio, isSlideshowOpen, isSlideshowPaused, slideshowIndex]);

  useEffect(() => {
    if (!isSlideshowOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    document.body.style.overflow = 'hidden';
    slideshowDialogRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previousActiveElement?.focus();
    };
  }, [isSlideshowOpen]);

  if (!selectedArtifact) {
    return <Empty description="This collection does not have any artifacts to display yet." />;
  }

  const transcriptCopy = getTranscriptCopy(selectedArtifact);
  const hasReadyImage = selectedArtifact.image?.status === 'READY' && Boolean(selectedArtifact.image?.url);
  const descriptionText = selectedArtifact.description?.trim() || 'No written story was shared for this memory.';
  const transcriptText = transcriptCopy.body;
  const shouldShowDescriptionToggle = descriptionText.length > EXPANDABLE_TEXT_PREVIEW_LENGTH;
  const shouldShowTranscriptToggle = transcriptText.length > EXPANDABLE_TEXT_PREVIEW_LENGTH;
  const visibleDescriptionText =
    shouldShowDescriptionToggle && !isDescriptionExpanded
      ? `${descriptionText.slice(0, EXPANDABLE_TEXT_PREVIEW_LENGTH - 3).trimEnd()}...`
      : descriptionText;
  const visibleTranscriptText =
    shouldShowTranscriptToggle && !isTranscriptExpanded
      ? `${transcriptText.slice(0, EXPANDABLE_TEXT_PREVIEW_LENGTH - 3).trimEnd()}...`
      : transcriptText;

  const handleDownloadZip = async () => {
    if (!normalizedDownloadZipUrl || isZipDownloading) {
      return;
    }

    setIsZipDownloading(true);
    setZipDownloadError('');

    try {
      await downloadFileFromApi({
        url: normalizedDownloadZipUrl,
        fallbackFileName: `${sanitizeFilenamePart(patientName, 'shared-artifacts')}-artifacts.zip`,
        accessToken
      });
    } catch {
      setZipDownloadError('We couldn’t prepare the download. Please try again.');
    } finally {
      setIsZipDownloading(false);
    }
  };

  const renderArtifactStory = (
    artifact: Artifact,
    {
      descriptionBody,
      transcriptBody,
      descriptionToggle,
      transcriptToggle
    }: {
      descriptionBody: string;
      transcriptBody: string;
      descriptionToggle?: ReactNode;
      transcriptToggle?: ReactNode;
    }
  ) => {
    const hasArtifactAudio = artifact.audio?.status === 'READY' && Boolean(artifact.audio.url);
    const artifactTranscriptCopy = getTranscriptCopy(artifact);

    return (
      <>
        <div className="public-artifact-collection__memory-meta">
          <span>{getMemoryLabel(artifact)}</span>
          {artifact.audio && (
            <span className="public-artifact-collection__memory-meta-pill" style={audioPillStyle}>
              <AudioOutlined />
              Audio story
            </span>
          )}
        </div>

        <Title level={2} className="public-artifact-collection__memory-title">
          {artifact.title || 'Untitled artifact'}
        </Title>

        <div>
          <Paragraph className="public-artifact-collection__memory-description">{descriptionBody}</Paragraph>
          {descriptionToggle}
        </div>

        <div className="public-artifact-collection__audio-block">
          {hasArtifactAudio ? (
            <AppAudioPlayer
              url={artifact.audio?.url || ''}
              mimeType={artifact.audio?.mimeType || undefined}
              className="public-artifact-collection__audio-player"
              fallback={<Text>Audio temporarily unavailable.</Text>}
            />
          ) : artifact.audio ? (
            <Paragraph type="secondary" className="public-artifact-collection__audio-empty">
              Audio temporarily unavailable.
            </Paragraph>
          ) : (
            <Paragraph type="secondary" className="public-artifact-collection__audio-empty">
              No audio story was shared for this memory.
            </Paragraph>
          )}
        </div>

        <div className="public-artifact-collection__transcript-block">
          <div className="public-artifact-collection__transcript-heading">
            <FileTextOutlined />
            <Text strong>{artifactTranscriptCopy.heading}</Text>
            {artifact.transcript.status === 'COMPLETED' && (
              <CheckCircleFilled
                className="public-artifact-collection__transcript-status"
                style={sharedCheckIconStyle}
              />
            )}
          </div>

          <Paragraph className="public-artifact-collection__transcript-text">{transcriptBody}</Paragraph>
          {transcriptToggle}
        </div>
      </>
    );
  };

  return (
    <div className="public-artifact-collection" style={collectionStyle}>
      {showTopbar && (
        <header className="public-artifact-collection__topbar">
          <div className="public-artifact-collection__topbar-brand">
            <img src={topbarLogo.src} alt={topbarLogo.alt} className="public-artifact-collection__eternal-logo" />
          </div>

          <div className="public-artifact-collection__topbar-org">{organizationName}</div>

          <div className="public-artifact-collection__topbar-love" style={topbarLoveStyle}>
            <HeartOutlined />
            <span>Shared with love through Eternal</span>
          </div>
        </header>
      )}

      <section className="public-artifact-collection__hero">
        <div className="public-artifact-collection__hero-copy">
          <Title level={1} className="public-artifact-collection__hero-title">
            {heroTitle}
          </Title>
          <div className="public-artifact-collection__hero-divider" style={heroDividerStyle}>
            <span />
            <HeartOutlined className="public-artifact-collection__hero-divider-icon" />
            <span />
          </div>
          <Paragraph className="public-artifact-collection__hero-subtitle">{heroSubtitle}</Paragraph>
        </div>

        {(canDownloadCollection || keyedItems.length > 0) && (
          <div className="public-artifact-collection__hero-share">
            <div className="public-artifact-collection__hero-action-group">
              <Button
                icon={<PlayCircleOutlined />}
                className="public-artifact-collection__share-button public-artifact-collection__share-button--primary"
                style={primaryButtonStyle}
                onClick={handleOpenSlideshow}
                aria-label="Play slideshow"
              >
                Play slideshow
              </Button>
              <Text className="public-artifact-collection__share-note">Watch these memories in sequence</Text>
            </div>

            {canDownloadCollection && (
              <div className="public-artifact-collection__hero-action-group">
                <Button
                  icon={<DownloadOutlined />}
                  className="public-artifact-collection__share-button public-artifact-collection__share-button--secondary"
                  style={secondaryButtonStyle}
                  onClick={handleDownloadZip}
                  disabled={isZipDownloading}
                >
                  {isZipDownloading ? 'Preparing download...' : 'Download collection'}
                </Button>
                <Text className="public-artifact-collection__share-note">Download all artifacts as a zip file</Text>
                {zipDownloadError && (
                  <Text className="public-artifact-collection__share-feedback">{zipDownloadError}</Text>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="public-artifact-collection__spotlight-row">
        <article className="public-artifact-collection__spotlight-card">
          <div className="public-artifact-collection__spotlight-image-panel">
            {hasReadyImage ? (
              <button
                type="button"
                className="public-artifact-collection__spotlight-image-button"
                aria-label={`Expand ${selectedArtifact.title || 'artifact'}`}
                onClick={() => setIsExpandedArtifactOpen(true)}
              >
                <img
                  src={selectedArtifact.image?.url || ''}
                  alt={selectedArtifact.title || 'Selected artifact'}
                  className="public-artifact-collection__spotlight-image"
                />
              </button>
            ) : (
              <div className="public-artifact-collection__image-empty">
                <Empty description="Image temporarily unavailable." />
              </div>
            )}
          </div>

          <div className="public-artifact-collection__spotlight-content">
            {renderArtifactStory(selectedArtifact, {
              descriptionBody: visibleDescriptionText,
              transcriptBody: visibleTranscriptText,
              descriptionToggle: shouldShowDescriptionToggle ? (
                <button
                  type="button"
                  className="public-artifact-collection__transcript-toggle"
                  onClick={() => setIsDescriptionExpanded((previous) => !previous)}
                  aria-expanded={isDescriptionExpanded}
                >
                  {isDescriptionExpanded ? 'Hide description' : 'Show description'}
                </button>
              ) : undefined,
              transcriptToggle: shouldShowTranscriptToggle ? (
                <button
                  type="button"
                  className="public-artifact-collection__transcript-toggle"
                  onClick={() => setIsTranscriptExpanded((previous) => !previous)}
                  aria-expanded={isTranscriptExpanded}
                >
                  {isTranscriptExpanded ? 'Hide transcript' : 'Show transcript'}
                </button>
              ) : undefined
            })}
          </div>
        </article>
      </section>

      <section className="public-artifact-collection__rail-card">
        <div
          className={`public-artifact-collection__rail-grid${isSingleArtifact ? ' public-artifact-collection__rail-grid--single' : ''}`}
        >
          {keyedItems.map(({ artifact, renderKey }) => {
            const isSelected = renderKey === selectedItem?.renderKey;
            const thumbnailUrl = artifact.image?.thumbnailUrl || artifact.image?.url || '';

            return (
              <button
                key={renderKey}
                type="button"
                aria-pressed={isSelected}
                aria-label={`Select artifact ${artifact.title || 'Untitled artifact'}`}
                onClick={() => handleSelectArtifact(renderKey)}
                className={`public-artifact-collection__thumbnail${isSelected ? ' public-artifact-collection__thumbnail--selected' : ''}`}
              >
                <div className="public-artifact-collection__thumbnail-image-wrap">
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={artifact.title || 'Artifact thumbnail'}
                      className="public-artifact-collection__thumbnail-image"
                    />
                  ) : (
                    <Text type="secondary">No image</Text>
                  )}

                  {isSelected && (
                    <CheckCircleFilled
                      className="public-artifact-collection__thumbnail-check"
                      style={sharedCheckIconStyle}
                    />
                  )}
                </div>

                <span className="public-artifact-collection__thumbnail-title">
                  {artifact.title || 'Untitled artifact'}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {showKeepCard && !user && (
        <section
          className="public-artifact-collection__keep-card"
          aria-label="Create an account to keep these artifacts"
        >
          <div className="public-artifact-collection__keep-card-content">
            <HeartOutlined className="public-artifact-collection__keep-card-icon" style={keepCardHeartStyle} />
            <div className="public-artifact-collection__keep-card-copy">
              <div className="public-artifact-collection__keep-card-title">Keep these artifacts forever</div>
              <div className="public-artifact-collection__keep-card-subtitle">
                Create a free Eternal account to save this artifact collection to your personal inbox — so you can
                revisit it anytime, alongside all your other memories.
              </div>
            </div>
          </div>
          <Button
            type="primary"
            className="public-artifact-collection__keep-card-button"
            style={primaryButtonStyle}
            href={signupHref}
          >
            Create free account
          </Button>
        </section>
      )}

      <footer className="public-artifact-collection__footer" style={footerStyle}>
        <HeartOutlined />
        <span>Thank you for being part of this beautiful story. Forever remembered. Always loved.</span>
        <LinkOutlined className="public-artifact-collection__footer-link-icon" />
      </footer>

      <Modal
        open={isExpandedArtifactOpen}
        onCancel={() => setIsExpandedArtifactOpen(false)}
        footer={null}
        centered
        width={960}
        className="public-artifact-collection__story-modal"
        destroyOnHidden
      >
        <div className="public-artifact-collection__story-modal-layout">
          <div className="public-artifact-collection__story-modal-image-panel">
            {hasReadyImage ? (
              <img
                src={selectedArtifact.image?.url || ''}
                alt={selectedArtifact.title || 'Selected artifact'}
                className="public-artifact-collection__story-modal-image"
              />
            ) : (
              <div className="public-artifact-collection__image-empty">
                <Empty description="Image temporarily unavailable." />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {isSlideshowOpen && slideshowArtifact && (
        <div
          ref={slideshowDialogRef}
          className="public-artifact-collection__slideshow"
          role="dialog"
          aria-modal="true"
          aria-label="Artifact slideshow"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              closeSlideshow();
            }
          }}
          onMouseMove={handleSlideshowInteraction}
          onClick={handleSlideshowInteraction}
          onTouchStart={handleSlideshowInteraction}
        >
          {hasSlideshowReadyAudio && (
            <audio
              key={`${slideshowArtifact.audio?.url || ''}::${slideshowArtifact.audio?.mimeType || ''}`}
              ref={slideshowAudioRef}
              preload="auto"
              aria-hidden="true"
              className="public-artifact-collection__slideshow-audio"
              onEnded={handleAdvanceSlideshow}
              onError={() => setIsSlideshowAudioUnavailable(true)}
            >
              <source src={slideshowArtifact.audio?.url || ''} type={slideshowArtifact.audio?.mimeType || undefined} />
            </audio>
          )}

          <div
            className={`public-artifact-collection__slideshow-chrome${areSlideshowControlsVisible ? '' : ' public-artifact-collection__slideshow-chrome--hidden'}`}
          >
            <div className="public-artifact-collection__slideshow-progress">
              <div className="public-artifact-collection__slideshow-progress-copy">
                <Text className="public-artifact-collection__slideshow-kicker">Slideshow mode</Text>
                <Text className="public-artifact-collection__slideshow-count">
                  {slideshowIndex + 1} of {keyedItems.length}
                </Text>
              </div>
              <div className="public-artifact-collection__slideshow-progress-track" aria-hidden="true">
                <span
                  className="public-artifact-collection__slideshow-progress-fill"
                  style={{ width: `${slideshowProgressPercent}%` }}
                />
              </div>
            </div>
          </div>

          <div className="public-artifact-collection__slideshow-stage">
            {hasSlideshowReadyImage ? (
              <img
                src={slideshowArtifact.image?.url || ''}
                alt={slideshowArtifact.title || 'Slideshow artifact'}
                className="public-artifact-collection__slideshow-image"
              />
            ) : (
              <div className="public-artifact-collection__slideshow-image-empty">
                <Empty description="Image temporarily unavailable." />
              </div>
            )}
          </div>

          <div
            className={`public-artifact-collection__slideshow-chrome public-artifact-collection__slideshow-chrome--bottom${areSlideshowControlsVisible ? '' : ' public-artifact-collection__slideshow-chrome--hidden'}`}
          >
            <div className="public-artifact-collection__slideshow-caption">
              <Text className="public-artifact-collection__slideshow-memory-label">
                {getMemoryLabel(slideshowArtifact)}
              </Text>
              <Title level={2} className="public-artifact-collection__slideshow-title">
                {slideshowArtifact.title || 'Untitled artifact'}
              </Title>
              {slideshowDescription && (
                <Paragraph className="public-artifact-collection__slideshow-description">
                  {slideshowDescription}
                </Paragraph>
              )}
            </div>

            <div
              className="public-artifact-collection__slideshow-controls"
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                shape="circle"
                className="public-artifact-collection__slideshow-control"
                icon={<StepBackwardOutlined />}
                aria-label="Previous slide"
                onClick={handlePreviousSlideshow}
                disabled={slideshowIndex === 0}
              />
              <Button
                shape="circle"
                className="public-artifact-collection__slideshow-control"
                icon={isSlideshowPaused ? <PlayCircleOutlined /> : <PauseOutlined />}
                aria-label={isSlideshowPaused ? 'Resume slideshow' : 'Pause slideshow'}
                onClick={() => {
                  setIsSlideshowPaused((previous) => !previous);
                  setAreSlideshowControlsVisible(true);
                }}
              />
              <Button
                shape="circle"
                className="public-artifact-collection__slideshow-control"
                icon={<StepForwardOutlined />}
                aria-label="Next slide"
                onClick={handleAdvanceSlideshow}
                disabled={slideshowIndex >= keyedItems.length - 1}
              />
              <Button
                className="public-artifact-collection__slideshow-exit"
                icon={<CloseOutlined />}
                aria-label="Exit slideshow"
                onClick={closeSlideshow}
              >
                Exit full screen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicArtifactCollectionRenderer;
